import React, { useState, useEffect, useCallback } from 'react';
import { Flag, Loader2, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Panneau admin "Signalements" — tickets envoyés par les clients via le bouton
 * "Signaler" (vidéo/image d'exercice incorrecte pendant une séance). */
export const AdminVideoReportsPanel = ({ csrfToken, onChanged }) => {
  const [reports, setReports] = useState(null);
  const [filter, setFilter] = useState('open');
  const [resolving, setResolving] = useState(null);

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/video-reports?status=${filter === 'all' ? '' : filter}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setReports(d.reports || []))
      .catch(() => setReports([]));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    setResolving(id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/video-reports/${id}/resolve`, {
        method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrfToken },
      });
      if (!res.ok) throw new Error();
      toast.success('Signalement résolu.');
      load();
      if (onChanged) onChanged();
    } catch {
      toast.error('Erreur.');
    } finally {
      setResolving(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-video-reports-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold text-white flex items-center gap-2"><Flag className="w-4.5 h-4.5 text-rose-400" /> Signalements de problèmes vidéo/image</h3>
        <div className="flex gap-1.5">
          {['open', 'resolved', 'all'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`video-reports-filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === f ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`}>
              {f === 'open' ? 'Ouverts' : f === 'resolved' ? 'Résolus' : 'Tous'}
            </button>
          ))}
        </div>
      </div>

      {reports === null && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
      {reports && reports.length === 0 && <p className="text-sm text-zinc-500 py-8 text-center">Aucun signalement pour ce filtre.</p>}

      <div className="space-y-2">
        {(reports || []).map((r) => (
          <div key={r.id} className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-start justify-between gap-3" data-testid={`video-report-row-${r.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-white font-semibold text-sm">{r.exercise_name}</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === 'open' ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                  {r.status === 'open' ? 'Ouvert' : 'Résolu'}
                </span>
              </div>
              <p className="text-zinc-400 text-xs mb-1">« {r.reason} »</p>
              <p className="text-zinc-500 text-[11px]">Par {r.client_name || r.client_email} ({r.client_email}) · {new Date(r.created_at).toLocaleString('fr-CH')}</p>
              {(r.video_url || r.image_url) && (
                <a href={`${BACKEND_URL}${r.video_url || r.image_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline mt-1">
                  <ExternalLink className="w-3 h-3" /> Voir le média
                </a>
              )}
            </div>
            {r.status === 'open' && (
              <button onClick={() => resolve(r.id)} disabled={resolving === r.id} data-testid={`resolve-report-btn-${r.id}`}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-xs font-semibold rounded-lg disabled:opacity-50">
                {resolving === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Résolu
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminVideoReportsPanel;
